import { parse } from 'mathjs';

import { isNumber } from './coercion';
import { fractionSimplify } from './fractions';
import type { Fraction } from './types';
import { isArray } from './types';

export type AlgebraicExpression = string | readonly [string, string];

/**
 * Extract variable names ('x', 'y', 'z') from an expression string or
 * fraction-expression pair.
 */
export function getExpressionVars(expression: AlgebraicExpression): string[] {
  const vars: string[] = [];
  const symbols = ['x', 'y', 'z'];
  for (const letter of symbols) {
    if (isArray(expression)) {
      if (expression[0].includes(letter) || expression[1].includes(letter))
        vars.push(letter);
    } else if (typeof expression === 'string') {
      if (expression.includes(letter)) vars.push(letter);
    }
  }
  return vars;
}

/**
 * Evaluate an algebra expression with the given variable values.
 * Handles fraction-pair expressions recursively.
 */
export function evaluate(
  expression: AlgebraicExpression,
  varValues: Record<string, number | null>,
  simplify = true,
): Fraction | number {
  if (isArray(expression)) {
    const f: [number, number] = [
      evaluate(expression[0], varValues) as number,
      evaluate(expression[1], varValues) as number,
    ];
    return simplify ? fractionSimplify(f) : f;
  }
  return evaluateString(expression, varValues);
}

function evaluateString(
  expression: string,
  varValues: Record<string, number | null>,
): number {
  const paramValues = Object.assign({}, varValues);
  const parsed = parse(fixExpressionString(expression));
  const value: unknown = parsed.evaluate(paramValues as Record<string, number>);
  return isNumber(value) ? (value as number) : 0;
}

/* eslint-disable no-useless-escape */
export function fixExpressionString(expression: string): string {
  expression = expression.replace(/@/g, '^2');
  expression = expression.replace(/#/g, '^3');
  expression = expression.replace(/\$/g, '^4');
  expression = expression.replace(/%/g, '^5');
  expression = expression.replace(/\[/g, '(');
  expression = expression.replace(/\]/g, ')');
  expression = expression.replace(/(xx)\^([0-9])/g, 'x^1x^$2');
  expression = expression.replace(/(yy)\^([0-9])/g, 'y^1y^$2');
  expression = expression.replace(/(zz)\^([0-9])/g, 'z^1z^$2');
  expression = expression.replace(/([xyz])\^([0-9])/g, '($1^$2)');
  expression = expression.replace(
    /(\([xyz]\^[0-9]\))(\([xyz]\^[0-9]\))/g,
    '($1*$2)',
  );
  expression = expression.replace(/([0-9])\(([xyz])\^([0-9])\)/g, '($1*$2^$3)');
  let pattern = /([0-9xyz]+)([xyz])/g;
  let newExpression = expression;
  do {
    expression = newExpression;
    newExpression = expression.replace(pattern, '($1*$2)');
  } while (newExpression !== expression);
  pattern = /([0-9xyz])([\(])/g;
  expression = expression.replace(pattern, '$1*(');
  pattern = /([\)])([0-9xyz])/g;
  expression = expression.replace(pattern, ')*$2');
  expression = expression.replace(/\)\(/g, ')*(');
  expression = expression.replace(/ /g, '');
  return expression;
}
/* eslint-enable no-useless-escape */
